import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import {
  settingsInputMinHeightPx,
  settingsOverviewHelperFontSize,
  settingsSelectSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";
import { setDropdownWorkstationId } from "~/features/workstationConfig/workstationConfig.slice";
import { useSetThisWorkstation } from "~/hooks/useSetThisWorkstation";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { selectDropdownWorkstation } from "~/redux/selectors/workstationConfigSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

export function SiteWorkstationAssignment() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { successToast, errorToast } = useToast();
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const userFc = useAppSelector(selectUsersFulfillmentCenter);
  const { setThisWorkstation, isLoading: isSettingWorkstation } =
    useSetThisWorkstation();
  const dropdownWorkstation = useAppSelector(selectDropdownWorkstation);

  const { data: workstations, isLoading } = useGetWorkstationsQuery(undefined, {
    skip: !userFc
  });
  const sortedWorkstations = useMemo(
    () =>
      workstations === undefined
        ? undefined
        : workstations.toSorted(
            (a, b) =>
              a.autostoreGridName.localeCompare(b.autostoreGridName) ||
              a.deviceId.localeCompare(b.deviceId)
          ),
    [workstations]
  );

  const handleWorkstationChanged = useCallback(
    (e: SelectChangeEvent) => {
      dispatch(setDropdownWorkstationId(e.target.value));
    },
    [dispatch]
  );

  const isWorkstationDirty = dropdownWorkstation?.id !== siteWorkstation?.id;

  const handleSetThisWorkstationClicked = useCallback(async () => {
    if (!isWorkstationDirty) {
      return;
    }
    try {
      await setThisWorkstation(dropdownWorkstation ?? null, true);
      if (dropdownWorkstation) {
        successToast(t("workstation set"), {
          description: t("workstation set description", {
            workstation: dropdownWorkstation.deviceId
          })
        });
      } else {
        successToast(t("workstation unset"), {
          description: t("workstation unset description")
        });
      }
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  }, [
    isWorkstationDirty,
    setThisWorkstation,
    dropdownWorkstation,
    successToast,
    errorToast,
    t
  ]);

  const assignDisabled = !isWorkstationDirty || isSettingWorkstation;

  const helperText = t("select workstation id matches port helper");

  if (!sortedWorkstations && isLoading) {
    return (
      <Stack id="ws-site-assignment-loading" spacing={2}>
        <Typography variant="body2">{t("workstation")}</Typography>
      </Stack>
    );
  }

  if (!sortedWorkstations?.length && !isLoading) {
    return (
      <Stack id="ws-site-assignment-empty" spacing={2}>
        <Typography variant="body2">{t("workstation")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("no workstations found")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack id="ws-site-assignment-root" spacing={1} sx={{ width: "100%" }}>
      <Box
        id="ws-site-assignment-assign-row"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flexWrap: { md: "nowrap" },
          alignItems: { md: "center" },
          width: "100%",
          minWidth: 0,
          columnGap: { xs: 0, md: 2 },
          rowGap: { xs: 1.5, md: 0 }
        }}
      >
        <Box
          id="ws-site-assignment-control-cluster"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "center" },
            flex: { md: "1 1 auto" },
            minWidth: 0,
            width: "100%",
            gap: { xs: 1.5, md: 2 }
          }}
        >
          <Box
            id="ws-site-assignment-select-wrap"
            sx={{
              width: "100%",
              maxWidth: {
                xs: "100%",
                md: settingsSiteSelectMaxWidthPx
              },
              minWidth: 0,
              flex: { md: "0 1 auto" }
            }}
          >
            <FormControl fullWidth size="small">
              <Select
                id="assign-workstation-select-preview"
                fullWidth
                variant="outlined"
                value={dropdownWorkstation?.id ?? ""}
                onChange={handleWorkstationChanged}
                displayEmpty
                inputProps={{
                  "aria-label": String(t("workstation")),
                  "aria-describedby": "ws-site-assignment-helper"
                }}
                sx={settingsSelectSx}
                renderValue={(id) => {
                  if (id === "") {
                    return (
                      <Typography
                        variant="body2"
                        component="span"
                        color="text.secondary"
                      >
                        <em>{t("no workstation selected")}</em>
                      </Typography>
                    );
                  }
                  const ws = sortedWorkstations?.find((w) => w.id === id);
                  if (!ws) {
                    return "";
                  }
                  return (
                    <Typography variant="body2" component="span" noWrap>
                      {ws.deviceId}
                    </Typography>
                  );
                }}
              >
                {sortedWorkstations?.map((workstation) => (
                  <MenuItem key={workstation.id} value={workstation.id}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      width="100%"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ minWidth: 0 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0, flex: "1 1 auto" }}
                      >
                        <Typography variant="body2" noWrap>
                          {workstation.deviceId}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {workstation.autostoreGridName}
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={
                          workstation.status === "Active"
                            ? t("workstation status chip in use")
                            : workstation.status
                        }
                        color={
                          workstation.status === "Active"
                            ? "warning"
                            : undefined
                        }
                      />
                    </Stack>
                  </MenuItem>
                ))}
                <MenuItem value="">
                  <em>{t("no workstation selected")}</em>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            id="ws-site-assignment-button-wrap"
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-end", md: "flex-start" },
              alignItems: "center",
              flexShrink: 0,
              minWidth: 0
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              loading={isSettingWorkstation}
              disabled={assignDisabled}
              onClick={() => void handleSetThisWorkstationClicked()}
              aria-label={t("assign workstation")}
              sx={{
                flexShrink: 0,
                minHeight: settingsInputMinHeightPx,
                boxSizing: "border-box",
                maxWidth: "none",
                width: "auto",
                ...(assignDisabled && {
                  bgcolor: "transparent",
                  color: "secondary.main",
                  border: "1px solid",
                  borderColor: "secondary.main",
                  boxShadow: "none"
                })
              }}
            >
              {t("assign workstation")}
            </Button>
          </Box>
        </Box>
      </Box>

      <Typography
        id="ws-site-assignment-helper"
        variant="body2"
        sx={{
          color: "text.secondary",
          m: 0,
          display: "block",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          fontSize: settingsOverviewHelperFontSize,
          lineHeight: 1.4,
          pl: 0
        }}
      >
        {helperText}
      </Typography>
    </Stack>
  );
}
